import React, { useState, useMemo, useEffect } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { useNavigate } from 'react-router-dom';
import PageTitle from '@/components/Admin/PageTitle';
import { useDistributorStatus } from '@/hooks/useDistributorStatus';
import { useUIStore } from '@/store/useUIStore';
import {
   listInquiries as apiListInquiries,
   approveInquiry as apiApproveInquiry,
   fulfillInquiry as apiFulfillInquiry,
   rejectInquiry as apiRejectInquiry,
   listMyCreditPackages,
   getMyDistributorProfile as apiGetMyDistributorProfile,
   updateMyDistributorProfile as apiUpdateMyDistributorProfile,
   type ApiInquiry,
   type InquiryStatusApi,
   type ApiCreditPurchase,
   type CreditPurchaseStatus,
   type DistributorProfile,
   type DistributorProfilePatch,
   type DistributorBankAccount,
   type DistributorGcashAccount,
} from '@/api/vendo';
import type { ReceiptAccountFormData } from '@/components/Admin/Prompt/ReceiptAccountForm';
import FileUploader from '@/components/Admin/UI/FileUploader';
import PartnerShareLink from '@/components/Admin/PartnerShareLink';
import { PayLogo, PayLogoRow } from '@/components/Admin/PaymentLogos';
import SoldCreditsTable from '@/components/Admin/SoldCreditsTable';

type Tab = 'inquiries' | 'my-purchases' | 'transfers' | 'profile';

const VALID_TABS: Tab[] = ['inquiries', 'my-purchases', 'transfers', 'profile'];
const TAB_STORAGE_KEY = 'smartfi-distributor-tab';

export const loadTab = (): Tab => {
   // A notification can name the tab: /wifi-distributors?tab=my-purchases (Ray 2026-10-25).
   try {
      const q = new URLSearchParams(window.location.search).get('tab');
      if (q && (VALID_TABS as string[]).includes(q)) return q as Tab;
   } catch {}
   try {
      const v = localStorage.getItem(TAB_STORAGE_KEY);
      if (v && (VALID_TABS as string[]).includes(v)) return v as Tab;
   } catch {}
   return 'inquiries';
};

// Server populates buyer with `name email` on distributor-scope queries.
type PopulatedBuyer = { _id: string; name?: string; email?: string };

/* ─── page ─────────────────────────────────────────────────── */

const Distributor = () => {
   const queryClient = useQueryClient();
   const navigate = useNavigate();
   const { isDistributor, isLoading: isStatusLoading } = useDistributorStatus();
   const { handleShow } = useUIStore();
   // The "add a payment method" notice opens ?tab=profile (Ray 2026-10-27).
   const [tab, setTab] = useState<Tab>(() => {
      const q = new URLSearchParams(window.location.search).get('tab') as Tab | null;
      return q && VALID_TABS.includes(q) ? q : loadTab();
   });

   // Send distributors to the Buy Credits marketplace instead of a modal.
   const openAddCredits = () => navigate('/wifi-partners');
   const [priceOverrides, setPriceOverrides] = useState<Record<string, string>>({});

   useEffect(() => {
      try {
         localStorage.setItem(TAB_STORAGE_KEY, tab);
      } catch {}
   }, [tab]);

   const { data: inquiries = [], isLoading: inquiriesLoading } = useQuery({
      queryKey: ['vendo', 'inquiries', 'distributor'],
      queryFn: () => apiListInquiries({ scope: 'distributor' }),
   });

   const { data: myPurchasesRaw = [], isLoading: purchasesLoading } = useQuery({
      queryKey: ['vendo', 'my-credit-packages'],
      queryFn: listMyCreditPackages,
   });
   // Newest first — the list comes back unsorted.
   const myPurchases = useMemo(
      () =>
         [...myPurchasesRaw].sort(
            (a, b) =>
               new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
         ),
      [myPurchasesRaw]
   );

   const purchaseStats = useMemo(() => {
      const proofPending = myPurchases.filter((p) => p.status === 'pending').length;
      const awaitingAdmin = myPurchases.filter(
         (p) => p.status === 'payment-submitted'
      ).length;
      const approvedTotal = myPurchases
         .filter((p) => p.status === 'approved')
         .reduce((s, p) => s + p.credits, 0);
      const approvedSpend = myPurchases
         .filter((p) => p.status === 'approved')
         .reduce((s, p) => s + p.amount, 0);
      return { proofPending, awaitingAdmin, approvedTotal, approvedSpend };
   }, [myPurchases]);

   const invalidateInquiries = () =>
      queryClient.invalidateQueries({ queryKey: ['vendo', 'inquiries'] });

   const approveM = useMutation({
      mutationFn: ({ id, price }: { id: string; price: number }) =>
         apiApproveInquiry(id, price),
      onSuccess: invalidateInquiries,
   });

   const fulfillM = useMutation({
      mutationFn: (id: string) => apiFulfillInquiry(id),
      onSuccess: () => {
         invalidateInquiries();
         queryClient.invalidateQueries({ queryKey: ['credits'] });
      },
   });

   const rejectM = useMutation({
      mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
         apiRejectInquiry(id, reason),
      onSuccess: invalidateInquiries,
   });

   const stats = useMemo(() => {
      const pending = inquiries.filter((i) => i.status === 'pending');
      const approved = inquiries.filter((i) => i.status === 'approved');
      const fulfilled = inquiries.filter((i) => i.status === 'fulfilled');
      const totalFulfilledCredits = fulfilled.reduce((s, i) => s + i.requestedCredits, 0);
      const totalFulfilledRevenue = fulfilled.reduce(
         (s, i) =>
            s +
            i.requestedCredits *
               (i.negotiatedPricePerCredit ?? i.proposedPricePerCredit),
         0
      );
      return {
         pendingCount: pending.length,
         pendingCredits: pending.reduce((s, i) => s + i.requestedCredits, 0),
         approvedCount: approved.length,
         totalFulfilledCredits,
         totalFulfilledRevenue,
      };
   }, [inquiries]);

   // Two lists, no filter (Ray 2026-10-28: a filter that starts on "Pending" looked empty):
   // what needs a click (pending + approved), and the history (fulfilled + rejected).
   const needsAction = useMemo(
      () => inquiries.filter((i) => i.status === 'pending' || i.status === 'approved'),
      [inquiries]
   );
   const history = useMemo(
      () =>
         inquiries
            .filter((i) => i.status === 'fulfilled' || i.status === 'rejected')
            .sort((a, b) => (b.fulfilledAt ?? b.rejectedAt ?? b.updatedAt).localeCompare(a.fulfilledAt ?? a.rejectedAt ?? a.updatedAt)),
      [inquiries]
   );

   const handleApprove = (inq: ApiInquiry) => {
      const overrideRaw = priceOverrides[inq._id];
      const finalPrice =
         overrideRaw !== undefined && overrideRaw !== ''
            ? Number(overrideRaw)
            : inq.proposedPricePerCredit;
      if (!Number.isFinite(finalPrice) || finalPrice <= 0) {
         toast.error('Enter a valid per-credit price');
         return;
      }
      approveM.mutate(
         { id: inq._id, price: finalPrice },
         {
            onSuccess: () => {
               const negotiated = finalPrice !== inq.proposedPricePerCredit;
               toast.success(
                  `Approved · ${inq.requestedCredits} credits at ₱${finalPrice}/credit${
                     negotiated ? ' (negotiated)' : ''
                  }`,
                  { autoClose: 3000 }
               );
               setPriceOverrides((p) => {
                  const next = { ...p };
                  delete next[inq._id];
                  return next;
               });
            },
            onError: (err: any) => {
               toast.error(err?.response?.data?.error || 'Approve failed');
            },
         }
      );
   };

   const handleFulfill = (inq: ApiInquiry) => {
      fulfillM.mutate(inq._id, {
         onSuccess: () => {
            toast.success(
               `Fulfilled · ${inq.requestedCredits} credits transferred`,
               { autoClose: 3000 }
            );
         },
         onError: (err: any) => {
            const status = err?.response?.status;
            const msg =
               status === 402
                  ? 'Insufficient credits in your wallet to fulfill this inquiry.'
                  : err?.response?.data?.error || 'Fulfill failed';
            toast.error(msg);
         },
      });
   };

   const handleReject = (inq: ApiInquiry) => {
      const buyer = inq.buyer as unknown as PopulatedBuyer;
      const buyerName = buyer?.name || buyer?.email || 'this inquiry';
      handleShow({
         template: 'reject-reason',
         title: 'Reject inquiry',
         windowType: 'desktop',
         data: {
            prompt: `Reject ${buyerName}'s inquiry?`,
            hint: 'The buyer will see this reason on their My Inquiries list.',
            required: false,
            onSubmit: (reason: string) => {
               rejectM.mutate(
                  { id: inq._id, reason: reason || undefined },
                  {
                     onSuccess: () => {
                        toast.info('Inquiry rejected', { autoClose: 2000 });
                     },
                     onError: (err: any) => {
                        toast.error(err?.response?.data?.error || 'Reject failed');
                     },
                  }
               );
            },
         },
      });
   };

   const setOverride = (id: string, value: string) => {
      const v = value.replace(/[^0-9.]/g, '');
      setPriceOverrides((p) => ({ ...p, [id]: v }));
   };

   const buyerOf = (inq: ApiInquiry) => {
      const buyer = inq.buyer as unknown as PopulatedBuyer;
      return { name: buyer?.name || buyer?.email || 'Unknown buyer', email: buyer?.email ?? '' };
   };

   const renderActionRow = (inq: ApiInquiry) => {
      const overrideRaw = priceOverrides[inq._id];
      const isPending = inq.status === 'pending';
      const baselinePrice = inq.proposedPricePerCredit;
      const settledPrice = inq.negotiatedPricePerCredit ?? inq.proposedPricePerCredit;
      const effectivePrice = isPending
         ? overrideRaw !== undefined && overrideRaw !== ''
            ? Number(overrideRaw) || baselinePrice
            : baselinePrice
         : settledPrice;
      const total = inq.requestedCredits * effectivePrice;
      const negotiated = isPending && overrideRaw !== undefined && overrideRaw !== '' && Number(overrideRaw) !== baselinePrice;
      const b = buyerOf(inq);
      return (
         <DiTr key={inq._id} $status={inq.status}>
            <td data-label="Buyer">
               <DiName>{b.name}</DiName>
               {b.email && <DiSub>{b.email}</DiSub>}
               {inq.notes && <DiSub>“{inq.notes}”</DiSub>}
            </td>
            <td className="num" data-label="Credits">
               <DiNum>{inq.requestedCredits}</DiNum>
            </td>
            <td data-label="₱ / credit">
               {isPending ? (
                  <DiPriceEdit>
                     ₱
                     <PriceInput
                        aria-label="Price per credit"
                        value={overrideRaw ?? String(baselinePrice)}
                        placeholder={String(baselinePrice)}
                        onChange={(e) => setOverride(inq._id, e.target.value)}
                     />
                  </DiPriceEdit>
               ) : (
                  <DiNum>₱{settledPrice}</DiNum>
               )}
            </td>
            <td className="num" data-label="Total">
               <DiNum $hi={negotiated}>₱{total.toLocaleString()}</DiNum>
               {negotiated && <DiSub>was ₱{baselinePrice}</DiSub>}
            </td>
            <td data-label="When">
               <DiSub as="span">{formatRelative(inq.createdAt)}</DiSub>
            </td>
            <td data-label="Receipt">
               {inq.paymentProofUrl ? (
                  <DiLink href={inq.paymentProofUrl} target="_blank" rel="noopener noreferrer">
                     📎 View
                  </DiLink>
               ) : (
                  <DiSub as="span">none yet</DiSub>
               )}
            </td>
            <td className="acts">
               <DiActs>
                  <RejectBtn disabled={rejectM.isPending} onClick={() => handleReject(inq)}>
                     Reject
                  </RejectBtn>
                  {isPending ? (
                     <ApproveBtn disabled={approveM.isPending} onClick={() => handleApprove(inq)}>
                        ✓ Approve
                     </ApproveBtn>
                  ) : (
                     <ApproveBtn disabled={fulfillM.isPending} onClick={() => handleFulfill(inq)}>
                        ⇨ Fulfill
                     </ApproveBtn>
                  )}
               </DiActs>
               {!isPending && <DiSub>approved · fulfill once paid</DiSub>}
            </td>
         </DiTr>
      );
   };

   const renderHistoryRow = (inq: ApiInquiry) => {
      const settledPrice = inq.negotiatedPricePerCredit ?? inq.proposedPricePerCredit;
      const total = inq.requestedCredits * settledPrice;
      const b = buyerOf(inq);
      const when = inq.fulfilledAt ?? inq.rejectedAt ?? inq.updatedAt;
      return (
         <DiTr key={inq._id} $status={inq.status}>
            <td data-label="Buyer">
               <DiName>{b.name}</DiName>
               {b.email && <DiSub>{b.email}</DiSub>}
               {inq.status === 'rejected' && inq.notes && <DiReason title={inq.notes}>{inq.notes}</DiReason>}
            </td>
            <td className="num" data-label="Credits">
               <DiNum>{inq.requestedCredits}</DiNum>
            </td>
            <td className="num" data-label="Total">
               <DiNum>₱{total.toLocaleString()}</DiNum>
               <DiSub>₱{settledPrice} × {inq.requestedCredits}</DiSub>
            </td>
            <td data-label="Status">
               <DiPill $status={inq.status}>{inq.status === 'fulfilled' ? 'Fulfilled' : 'Rejected'}</DiPill>
            </td>
            <td data-label="When">
               <DiSub as="span">{formatRelative(when)}</DiSub>
            </td>
            <td data-label="Receipt">
               {inq.paymentProofUrl && (
                  <DiLink href={inq.paymentProofUrl} target="_blank" rel="noopener noreferrer">
                     📎 View
                  </DiLink>
               )}
            </td>
         </DiTr>
      );
   };

   // Route-level gate: only approved distributors can use this page.
   // Loading guard prevents the empty-state flash before the status query
   // resolves on first navigation.
   if (!isStatusLoading && !isDistributor) {
      return (
         <Wrapper>
            <PageTitle title="Distributor" actions={[]} />
            <NotDistributorBanner>
               <NdbIcon>🔒</NdbIcon>
               <NdbBody>
                  <NdbTitle>This page is for approved SmartFi Distributors</NdbTitle>
                  <NdbText>
                     Apply via the Buy Credits page — once admin approves your
                     application, the Distributor menu will appear in your sidebar.
                  </NdbText>
               </NdbBody>
               <NdbBtn onClick={() => navigate('/partners')}>Open Buy Credits</NdbBtn>
            </NotDistributorBanner>
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         <PageTitle
            title="Distributor"
            actions={[{ type: 'button', title: '+ Add Credits', onClick: openAddCredits }]}
         />

         <TabBar>
            <TabBtn $active={tab === 'inquiries'} onClick={() => setTab('inquiries')}>
               Customer Inquiries
               {stats.pendingCount > 0 && (
                  <TabBadge>{stats.pendingCount}</TabBadge>
               )}
            </TabBtn>
            <TabBtn $active={tab === 'my-purchases'} onClick={() => setTab('my-purchases')}>
               My SmartFi Purchases
               {purchaseStats.awaitingAdmin + purchaseStats.proofPending > 0 && (
                  <TabBadge>
                     {purchaseStats.awaitingAdmin + purchaseStats.proofPending}
                  </TabBadge>
               )}
            </TabBtn>
            {/* Repurposed from the old "Transfer Credits" tab: it rendered
                wallet transfers, which NEVER contain sales (inquiry-fulfill
                writes CreditLedger rows, not CreditTransfer) — so for a
                distributor it was always empty. Wallet transfers still live
                in My Points; this tab now shows actual completed sales. */}
            <TabBtn $active={tab === 'transfers'} onClick={() => setTab('transfers')}>
               Sold Credits
            </TabBtn>
            <TabBtn $active={tab === 'profile'} onClick={() => setTab('profile')}>
               Profile
            </TabBtn>
         </TabBar>

         {tab === 'my-purchases' && (
            <MyPurchasesPane
               purchases={myPurchases}
               loading={purchasesLoading}
               stats={purchaseStats}
               onShop={() => navigate('/partners')}
            />
         )}

         {tab === 'transfers' && <SoldCreditsTable />}

         {tab === 'profile' && <ProfilePane />}

         {tab === 'inquiries' && (
            <>
               {/* Compact (Ray 2026-10-28): summary line, "Needs your action" on top, history under it. */}
               <DiSummary data-testid="di-summary">
                  <DiChip $tone="warn">
                     <b>{stats.pendingCount}</b> pending · {stats.pendingCredits} credits requested
                  </DiChip>
                  <DiChip $tone="brand">
                     <b>{stats.approvedCount}</b> approved · to fulfill
                  </DiChip>
                  <DiChip $tone="ok">
                     <b>{stats.totalFulfilledCredits.toLocaleString()}</b> credits fulfilled · ₱{stats.totalFulfilledRevenue.toLocaleString()}
                  </DiChip>
               </DiSummary>

               <DiPanel $tone="warn" data-testid="needs-action">
                  <DiPanelHead>
                     <DiPanelTitle>
                        Needs your action <DiCount>{needsAction.length}</DiCount>
                     </DiPanelTitle>
                     <DiPanelHint>
                        Approve a new inquiry (change the ₱/credit first if you agreed on another price), then Fulfill once the buyer's payment is in — that transfers the credits.
                     </DiPanelHint>
                  </DiPanelHead>
                  {inquiriesLoading && inquiries.length === 0 ? (
                     <SkeletonLines lines={2} />
                  ) : needsAction.length === 0 ? (
                     <DiEmpty>Nothing waiting — new inquiries show up here.</DiEmpty>
                  ) : (
                     <DiTable>
                        <thead>
                           <tr>
                              <th>Buyer</th>
                              <th className="num">Credits</th>
                              <th>₱ / credit</th>
                              <th className="num">Total</th>
                              <th>When</th>
                              <th>Receipt</th>
                              <th />
                           </tr>
                        </thead>
                        <tbody>{needsAction.map(renderActionRow)}</tbody>
                     </DiTable>
                  )}
               </DiPanel>

               <DiPanel data-testid="history">
                  <DiPanelHead>
                     <DiPanelTitle>
                        Fulfilled &amp; rejected <DiCount $muted>{history.length}</DiCount>
                     </DiPanelTitle>
                  </DiPanelHead>
                  {history.length === 0 ? (
                     <DiEmpty>No history yet.</DiEmpty>
                  ) : (
                     <DiTable>
                        <thead>
                           <tr>
                              <th>Buyer</th>
                              <th className="num">Credits</th>
                              <th className="num">Total</th>
                              <th>Status</th>
                              <th>When</th>
                              <th>Receipt</th>
                           </tr>
                        </thead>
                        <tbody>{history.map(renderHistoryRow)}</tbody>
                     </DiTable>
                  )}
               </DiPanel>
            </>
         )}
      </Wrapper>
   );
};

/* ─── My SmartFi Purchases pane ────────────────────────────── */

interface MyPurchasesPaneProps {
   purchases: ApiCreditPurchase[];
   loading: boolean;
   stats: {
      proofPending: number;
      awaitingAdmin: number;
      approvedTotal: number;
      approvedSpend: number;
   };
   onShop: () => void;
}

const PURCHASE_STATUS_LABEL: Record<CreditPurchaseStatus, string> = {
   pending: 'Awaiting payment proof',
   'payment-submitted': 'Awaiting admin approval',
   approved: 'Credited',
   rejected: 'Rejected',
};

const MyPurchasesPane = ({ purchases, loading, stats, onShop }: MyPurchasesPaneProps) => (
   <>
      <StatsRow>
         <StatCard>
            <StatLbl>Awaiting your proof</StatLbl>
            <StatVal>{stats.proofPending}</StatVal>
            <StatSub>started but not submitted</StatSub>
         </StatCard>
         <StatCard>
            <StatLbl>Awaiting admin</StatLbl>
            <StatVal>{stats.awaitingAdmin}</StatVal>
            <StatSub>proof submitted, pending review</StatSub>
         </StatCard>
         <StatCard>
            <StatLbl>Credits purchased</StatLbl>
            <StatVal $green>{stats.approvedTotal.toLocaleString()}</StatVal>
            <StatSub>₱{stats.approvedSpend.toLocaleString()} all-time</StatSub>
         </StatCard>
      </StatsRow>

      {stats.proofPending > 0 && (
         <ProofReminder>
            ⚠ You have <strong>{stats.proofPending}</strong> purchase
            {stats.proofPending === 1 ? '' : 's'} where you started checkout but
            never uploaded payment proof. These won't be reviewed until proof is
            submitted. (For now, restart the purchase from Buy Credits — in-place
            resume is on the roadmap.)
         </ProofReminder>
      )}

      <PurchaseTable>
         <PurchaseHead>
            <PurchaseHeadCell>Tier</PurchaseHeadCell>
            <PurchaseHeadCell>Credits</PurchaseHeadCell>
            <PurchaseHeadCell>Amount</PurchaseHeadCell>
            <PurchaseHeadCell>Status</PurchaseHeadCell>
            <PurchaseHeadCell>Method</PurchaseHeadCell>
            <PurchaseHeadCell>When</PurchaseHeadCell>
         </PurchaseHead>
         {loading && purchases.length === 0 && (
            <EmptyState>
               <EmptyIcon>⏳</EmptyIcon>
               <SkeletonLines lines={3} />
            </EmptyState>
         )}
         {!loading && purchases.length === 0 && (
            <EmptyState>
               <EmptyIcon>🛒</EmptyIcon>
               <EmptyText>No SmartFi credit purchases yet.</EmptyText>
               <ShopBtn onClick={onShop}>Browse credit packages →</ShopBtn>
            </EmptyState>
         )}
         {purchases.map((p) => {
            const when =
               p.approvedAt ?? p.proofSubmittedAt ?? p.paidAt ?? p.createdAt;
            return (
               <PurchaseRow key={p._id} $status={p.status}>
                  <PurchaseCell>
                     <TierLabel>{p.tier}</TierLabel>
                  </PurchaseCell>
                  <PurchaseCell>
                     <strong>{p.credits.toLocaleString()}</strong>
                  </PurchaseCell>
                  <PurchaseCell>₱{p.amount.toLocaleString()}</PurchaseCell>
                  <PurchaseCell>
                     <PurchaseStatusBadge $status={p.status}>
                        {PURCHASE_STATUS_LABEL[p.status]}
                     </PurchaseStatusBadge>
                     {p.status === 'rejected' && p.rejectedAt && (
                        <SmallSub>
                           on{' '}
                           {new Date(p.rejectedAt).toLocaleDateString('en-PH', {
                              month: 'short',
                              day: 'numeric',
                           })}
                        </SmallSub>
                     )}
                  </PurchaseCell>
                  <PurchaseCell>
                     {p.paymentMethod === 'gcash'
                        ? '📱 GCash'
                        : p.paymentMethod === 'bank-transfer'
                        ? '🏦 Bank'
                        : '—'}
                     {p.referenceNumber && (
                        <SmallSub>ref {p.referenceNumber}</SmallSub>
                     )}
                  </PurchaseCell>
                  <PurchaseCell>
                     {formatRelative(when)}
                  </PurchaseCell>
               </PurchaseRow>
            );
         })}
      </PurchaseTable>
   </>
);

/* ─── Profile pane (self-service) ─────────────────────────── */

const ProfilePane = () => {
   const queryClient = useQueryClient();
   const { handleShow } = useUIStore();

   const { data: profile, isLoading } = useQuery({
      queryKey: ['vendo', 'distributor', 'me-profile'],
      queryFn: apiGetMyDistributorProfile,
   });

   const [draft, setDraft] = useState<DistributorProfilePatch | null>(null);

   useEffect(() => {
      if (!profile) return;
      setDraft({
         displayName: profile.displayName,
         blurb: profile.blurb ?? '',
         photoUrl: profile.photoUrl ?? '',
         contactPhone: profile.contactPhone ?? '',
         facebookUrl: profile.facebookUrl ?? '',
         defaultPricePerCredit: profile.defaultPricePerCredit,
         bankAccounts: profile.bankAccounts ?? [],
         gcashAccounts: profile.gcashAccounts ?? [],
      });
   }, [profile]);

   const saveM = useMutation({
      mutationFn: (patch: DistributorProfilePatch) =>
         apiUpdateMyDistributorProfile(patch),
      onSuccess: () => {
         queryClient.invalidateQueries({
            queryKey: ['vendo', 'distributor', 'me-profile'],
         });
         queryClient.invalidateQueries({ queryKey: ['vendo', 'partners'] });
         toast.success('Profile saved', { autoClose: 2000 });
      },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error || 'Failed to save profile');
      },
   });

   const dirty = useMemo(() => {
      if (!profile || !draft) return false;
      return (
         draft.displayName !== profile.displayName ||
         (draft.blurb ?? '') !== (profile.blurb ?? '') ||
         (draft.photoUrl ?? '') !== (profile.photoUrl ?? '') ||
         (draft.contactPhone ?? '') !== (profile.contactPhone ?? '') ||
         (draft.facebookUrl ?? '') !== (profile.facebookUrl ?? '') ||
         draft.defaultPricePerCredit !== profile.defaultPricePerCredit ||
         JSON.stringify(draft.bankAccounts ?? []) !==
            JSON.stringify(profile.bankAccounts ?? []) ||
         JSON.stringify(draft.gcashAccounts ?? []) !==
            JSON.stringify(profile.gcashAccounts ?? [])
      );
   }, [draft, profile]);

   const openAddAccountModal = () => {
      const onSubmit: ReceiptAccountFormData['onSubmit'] = (result) => {
         setDraft((d) => {
            if (!d) return d;
            if (result.kind === 'bank') {
               return {
                  ...d,
                  bankAccounts: [...(d.bankAccounts ?? []), result.entry],
               };
            }
            return {
               ...d,
               gcashAccounts: [...(d.gcashAccounts ?? []), result.entry],
            };
         });
      };
      handleShow({
         template: 'receipt-account-form',
         title: 'Add a payment method',
         windowType: 'desktop',
         data: { onSubmit, allowMaya: true, intro: 'Where your buyers send payment for credits. You can add more than one.' },
      });
   };

   const removeBank = (i: number) =>
      setDraft((d) =>
         d
            ? { ...d, bankAccounts: (d.bankAccounts ?? []).filter((_, idx) => idx !== i) }
            : d
      );
   const removeGcash = (i: number) =>
      setDraft((d) =>
         d
            ? {
                 ...d,
                 gcashAccounts: (d.gcashAccounts ?? []).filter((_, idx) => idx !== i),
              }
            : d
      );

   const handleSave = () => {
      if (!draft || !dirty) return;
      if (!draft.displayName || !draft.displayName.trim()) {
         toast.error('Display name is required');
         return;
      }
      if (
         draft.defaultPricePerCredit === undefined ||
         draft.defaultPricePerCredit <= 0
      ) {
         toast.error('Default ₱/credit must be greater than 0');
         return;
      }
      saveM.mutate({
         displayName: draft.displayName.trim(),
         blurb: (draft.blurb ?? '').trim(),
         photoUrl: (draft.photoUrl ?? '').trim(),
         contactPhone: (draft.contactPhone ?? '').trim(),
         facebookUrl: (draft.facebookUrl ?? '').trim(),
         defaultPricePerCredit: draft.defaultPricePerCredit,
         bankAccounts: draft.bankAccounts ?? [],
         gcashAccounts: draft.gcashAccounts ?? [],
      });
   };

   if (isLoading || !profile || !draft) {
      return (
         <ProfileWrap>
            <SkeletonLines lines={3} />
         </ProfileWrap>
      );
   }

   const totalAccounts =
      (draft.bankAccounts?.length ?? 0) + (draft.gcashAccounts?.length ?? 0);

   return (
      <ProfileWrap>
         <ProfileColumns>
            {/* LEFT — who you are + how buyers pay you */}
            <ProfileCol>
               <ProfileField>
                  <ProfileLbl>Logo</ProfileLbl>
                  <ProfileSub>
                     Shown on the Partners marketplace next to your name.
                  </ProfileSub>
                  <FileUploader
                     value={draft.photoUrl ?? ''}
                     onChange={(url) =>
                        setDraft((d) => (d ? { ...d, photoUrl: url } : d))
                     }
                  />
               </ProfileField>

               <ProfileField>
                  <ProfileLbl>Short blurb</ProfileLbl>
                  <ProfileSub>
                     A line buyers see on your marketplace card. Optional.
                  </ProfileSub>
                  <ProfileTextarea
                     rows={3}
                     value={draft.blurb ?? ''}
                     placeholder="e.g. Fast credit drops · Cebu City · all day support"
                     onChange={(e) =>
                        setDraft((d) => (d ? { ...d, blurb: e.target.value } : d))
                     }
                  />
               </ProfileField>

               <ProfileField>
                  <ProfileLbl>Contact phone</ProfileLbl>
                  <ProfileSub>
                     Shown to buyers in the Request Credits modal so they can reach
                     you.
                  </ProfileSub>
                  <ProfileInput
                     value={draft.contactPhone ?? ''}
                     placeholder="e.g. +63 917 555 1234"
                     onChange={(e) =>
                        setDraft((d) => (d ? { ...d, contactPhone: e.target.value } : d))
                     }
                  />
               </ProfileField>

               {/* Payment methods (Ray 2026-10-27): it has to read as payments at a glance — and an
                   empty one is a blocker for every buyer, so it says so loudly. */}
               <PayCard $empty={totalAccounts === 0} data-testid="payment-methods">
                  <PayHead>
                     <div>
                        <ProfileLbl>💳 How buyers pay you ({totalAccounts})</ProfileLbl>
                        <ProfileSub>Shown in the Buy / Inquire window so buyers know where to send payment.</ProfileSub>
                     </div>
                     <PayLogoRow size={30} />
                  </PayHead>

                  {totalAccounts === 0 ? (
                     <PayEmpty role="alert">
                        <b>⚠️ Buyers can't pay you yet</b>
                        <span>Add GCash, Maya or a bank account — without one, no one can buy credits from you.</span>
                        <PayAddBig type="button" onClick={openAddAccountModal}>
                           + Add payment method
                        </PayAddBig>
                     </PayEmpty>
                  ) : (
                     <>
                        <ProfileAccountsList>
                           {(draft.bankAccounts ?? []).map((b, i) => (
                              <ProfileAccountRow key={`bank-${i}`}>
                                 <PayLogo kind="bank" size={34} />
                                 <ProfileAccountInfo>
                                    <ProfileAccountName>{b.bankName}</ProfileAccountName>
                                    <ProfileAccountMono>{b.accountNumber}</ProfileAccountMono>
                                    <ProfileAccountSub>{b.accountName}</ProfileAccountSub>
                                 </ProfileAccountInfo>
                                 <ProfileAccountRemove type="button" onClick={() => removeBank(i)} aria-label="Remove bank account">
                                    ✕
                                 </ProfileAccountRemove>
                              </ProfileAccountRow>
                           ))}
                           {(draft.gcashAccounts ?? []).map((g, i) => {
                              const maya = (g as { provider?: string }).provider === 'maya';
                              return (
                                 <ProfileAccountRow key={`gcash-${i}`}>
                                    <PayLogo kind={maya ? 'maya' : 'gcash'} size={34} />
                                    <ProfileAccountInfo>
                                       <ProfileAccountName>{maya ? 'Maya' : 'GCash'}</ProfileAccountName>
                                       <ProfileAccountMono>{g.number}</ProfileAccountMono>
                                       <ProfileAccountSub>{g.name}</ProfileAccountSub>
                                    </ProfileAccountInfo>
                                    <ProfileAccountRemove type="button" onClick={() => removeGcash(i)} aria-label={`Remove ${maya ? 'Maya' : 'GCash'} account`}>
                                       ✕
                                    </ProfileAccountRemove>
                                 </ProfileAccountRow>
                              );
                           })}
                        </ProfileAccountsList>
                        <ProfileAddBtn type="button" onClick={openAddAccountModal} style={{ justifySelf: 'start' }}>
                           + Add another
                        </ProfileAddBtn>
                     </>
                  )}
               </PayCard>
            </ProfileCol>

            {/* RIGHT — your shareable link + selling terms */}
            <ProfileCol>
               <PartnerShareLink slug={profile.slug} />

               <ProfileField>
                  <ProfileLbl>Display name *</ProfileLbl>
                  <ProfileSub>The name buyers see on your public link and card.</ProfileSub>
                  <ProfileInput
                     value={draft.displayName ?? ''}
                     onChange={(e) =>
                        setDraft((d) => (d ? { ...d, displayName: e.target.value } : d))
                     }
                  />
               </ProfileField>

               <ProfileField>
                  <ProfileLbl>Default ₱/credit *</ProfileLbl>
                  <ProfileSub>
                     Your shop-front price per credit. Buyers can still negotiate per
                     inquiry — this is the starting point.
                  </ProfileSub>
                  <ProfileInput
                     type="number"
                     step="0.01"
                     value={String(draft.defaultPricePerCredit ?? '')}
                     onChange={(e) =>
                        setDraft((d) =>
                           d
                              ? {
                                   ...d,
                                   defaultPricePerCredit: Number(e.target.value) || 0,
                                }
                              : d
                        )
                     }
                  />
               </ProfileField>

               <ProfileField>
                  <ProfileLbl>Facebook URL</ProfileLbl>
                  <ProfileSub>
                     Link to your Facebook page or profile. Buyers see this in the
                     Request Credits modal.
                  </ProfileSub>
                  <ProfileInput
                     value={draft.facebookUrl ?? ''}
                     placeholder="https://facebook.com/your-page"
                     onChange={(e) =>
                        setDraft((d) => (d ? { ...d, facebookUrl: e.target.value } : d))
                     }
                  />
               </ProfileField>
            </ProfileCol>
         </ProfileColumns>

         <ProfileSaveBar>
            <ProfileSaveHint>
               {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
            </ProfileSaveHint>
            <ProfileSaveBtn
               type="button"
               disabled={!dirty || saveM.isPending}
               onClick={handleSave}
            >
               {saveM.isPending ? 'Saving…' : 'Save changes'}
            </ProfileSaveBtn>
         </ProfileSaveBar>
      </ProfileWrap>
   );
};

/* ─── helpers ───────────────────────────────────────────────── */

function formatRelative(iso: string): string {
   const then = new Date(iso).getTime();
   const diff = Date.now() - then;
   const min = Math.floor(diff / 60000);
   if (min < 1) return 'just now';
   if (min < 60) return `${min}m ago`;
   const hr = Math.floor(min / 60);
   if (hr < 24) return `${hr}h ago`;
   const d = Math.floor(hr / 24);
   return `${d}d ago`;
}

/* ─── styles ────────────────────────────────────────────────── */

const Wrapper = styled.div`
   padding-bottom: 2rem;
`;

/* Tabs */

const TabBar = styled.div`
   display: flex;
   gap: 4px;
   padding: 0 1rem;
   margin-bottom: 16px;
   border-bottom: 1px solid ${colors.GRAY12};
   overflow-x: auto;
   flex-wrap: nowrap;
   -webkit-overflow-scrolling: touch;
   scrollbar-width: none;
   &::-webkit-scrollbar { display: none; }
   @media (max-width: 768px) { padding: 0 12px; }
`;

const TabBtn = styled.button<{ $active: boolean }>`
   flex-shrink: 0;
   white-space: nowrap;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 10px 16px;
   background: none;
   border: none;
   font-size: 13px;
   font-weight: ${({ $active }) => ($active ? 700 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   cursor: pointer;
   margin-bottom: -1px;
   transition: all 0.15s;
   &:hover { color: ${colors.PRIMARY}; }
`;

const TabBadge = styled.span`
   display: inline-grid;
   place-items: center;
   min-width: 18px;
   height: 18px;
   padding: 0 5px;
   border-radius: 999px;
   background: ${colors.WARNING};
   color: white;
   font-size: 10px;
   font-weight: 800;
`;

/* My SmartFi Purchases */

const ProofReminder = styled.div`
   margin: 0 1rem 12px;
   padding: 10px 14px;
   border-radius: 8px;
   background: ${colors.WARNING}10;
   border: 1px solid ${colors.WARNING}30;
   color: ${colors.GRAY_DARK};
   font-size: 12px;
   line-height: 1.5;
   @media (max-width: 768px) { margin: 0 12px 12px; }
`;

const PurchaseTable = styled.div`
   margin: 0 1rem;
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   overflow: hidden;
   @media (max-width: 768px) { margin: 0 12px; }
`;

const purchaseGrid = '90px 90px 110px 1.4fr 1fr 1fr';

const PurchaseHead = styled.div`
   display: grid;
   grid-template-columns: ${purchaseGrid};
   gap: 8px;
   padding: 8px 12px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
`;

const PurchaseHeadCell = styled.div`
   font-size: 10px;
   font-weight: 800;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const PurchaseRow = styled.div<{ $status: CreditPurchaseStatus }>`
   display: grid;
   grid-template-columns: ${purchaseGrid};
   gap: 8px;
   align-items: center;
   padding: 10px 12px;
   border-top: 1px solid ${colors.GRAY13};
   border-left: 3px solid
      ${({ $status }) =>
         $status === 'approved'
            ? colors.SUCCESS
            : $status === 'payment-submitted'
            ? colors.WARNING
            : $status === 'pending'
            ? colors.GRAY10
            : colors.DANGER};
   &:first-of-type { border-top: none; }
   &:hover { background: ${colors.PRIMARY}06; }
`;

const PurchaseCell = styled.div`
   font-size: 12px;
   color: ${colors.GRAY_DARK};
   display: flex;
   flex-direction: column;
   gap: 2px;
   overflow: hidden;
`;

const TierLabel = styled.span`
   font-size: 11px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${colors.PRIMARY};
`;

const PurchaseStatusBadge = styled.span<{ $status: CreditPurchaseStatus }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 8px;
   font-size: 10px;
   font-weight: 700;
   width: fit-content;
   background: ${({ $status }) =>
      $status === 'approved'
         ? `${colors.SUCCESS}18`
         : $status === 'payment-submitted'
         ? `${colors.WARNING}18`
         : $status === 'pending'
         ? colors.GRAY13
         : `${colors.DANGER}18`};
   color: ${({ $status }) =>
      $status === 'approved'
         ? colors.SUCCESS
         : $status === 'payment-submitted'
         ? colors.WARNING
         : $status === 'pending'
         ? colors.GRAY3
         : colors.DANGER};
`;

const SmallSub = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
`;

const ShopBtn = styled.button`
   margin-top: 8px;
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   padding: 8px 14px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover { opacity: 0.9; }
`;

const NotDistributorBanner = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 16px;
   margin: 0 1rem 16px;
   background: ${colors.WARNING}10;
   border: 1px solid ${colors.WARNING}30;
   border-radius: 10px;
   flex-wrap: wrap;
   @media (max-width: 768px) { margin: 0 12px 16px; }
`;

const NdbIcon = styled.span`
   font-size: 24px;
`;

const NdbBody = styled.div`
   flex: 1;
   min-width: 200px;
`;

const NdbTitle = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const NdbText = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.4;
`;

const NdbBtn = styled.button`
   background: ${colors.WARNING};
   color: #422006;
   border: none;
   padding: 6px 12px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover { opacity: 0.9; }
`;

const StatsRow = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 10px;
   padding: 0 1rem;
   margin-bottom: 16px;
   @media (max-width: 768px) { grid-template-columns: 1fr; padding: 0 12px; }
`;

const StatCard = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   padding: 12px 14px;
`;

const StatLbl = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
   margin-bottom: 4px;
`;

const StatVal = styled.p<{ $green?: boolean; $brand?: boolean }>`
   font-size: 22px;
   font-weight: 800;
   color: ${({ $green, $brand }) =>
      $green ? colors.SUCCESS : $brand ? colors.PRIMARY : colors.GRAY_DARK};
`;

const StatSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const diTone = { warn: colors.WARNING, brand: colors.PRIMARY, ok: colors.SUCCESS } as const;
const diStatus: Record<InquiryStatusApi, string> = {
   pending: colors.WARNING,
   approved: colors.PRIMARY,
   fulfilled: colors.SUCCESS,
   rejected: colors.DANGER,
};
const DiSummary = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
   margin-bottom: 12px;
   font-size: 12.5px;
`;
const DiChip = styled.span<{ $tone: keyof typeof diTone }>`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 4px 10px;
   border-radius: 999px;
   border: 1px solid ${({ $tone }) => diTone[$tone]}55;
   background: ${({ $tone }) => diTone[$tone]}14;
   b { color: ${({ $tone }) => diTone[$tone]}; font-size: 13px; }
`;
const DiPanel = styled.section<{ $tone?: 'warn' }>`
   margin-bottom: 14px;
   padding: 12px 14px;
   border-radius: 12px;
   border: 1px solid ${({ $tone }) => ($tone ? colors.WARNING + '66' : colors.GRAY13)};
   background: ${({ $tone }) => ($tone ? `linear-gradient(180deg, ${colors.WARNING}10, transparent 60%)` : 'transparent')};
`;
const DiPanelHead = styled.div`
   margin-bottom: 8px;
`;
const DiPanelTitle = styled.h3`
   margin: 0;
   font-size: 15px;
   font-weight: 800;
   display: flex;
   align-items: center;
   gap: 8px;
`;
const DiCount = styled.span<{ $muted?: boolean }>`
   padding: 1px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
   color: ${({ $muted }) => ($muted ? colors.GRAY3 : colors.WARNING)};
   background: ${({ $muted }) => ($muted ? colors.GRAY14 : colors.WARNING + '22')};
`;
const DiPanelHint = styled.p`
   margin: 4px 0 0;
   font-size: 12.5px;
   color: ${colors.GRAY3};
`;
const DiEmpty = styled.p`
   margin: 0;
   padding: 10px 0 4px;
   font-size: 13px;
   color: ${colors.GRAY3};
`;
const DiTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
   th {
      text-align: left;
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: ${colors.GRAY3};
      padding: 6px 10px;
      border-bottom: 1px solid ${colors.GRAY13};
      white-space: nowrap;
   }
   td {
      padding: 8px 10px;
      vertical-align: middle;
      border-bottom: 1px solid ${colors.GRAY13};
   }
   tbody tr:last-child td { border-bottom: 0; }
   .num { text-align: right; white-space: nowrap; }
   .acts { text-align: right; white-space: nowrap; }
   @media (max-width: 768px) {
      /* Phone (Ray 2026-10-28): a 3-line card per row — who + status, the numbers, then the actions. No column labels. */
      thead { display: none; }
      tbody tr {
         display: grid;
         /* The first column shrinks (minmax 0) so a long place name wraps instead of pushing the status and time off-screen. */
         grid-template-columns: minmax(0, 1fr) auto auto;
         grid-template-areas: 'who who status' 'credits total when' 'price price price' 'receipt receipt receipt' 'acts acts acts';
         column-gap: 10px;
         row-gap: 3px;
         align-items: center;
         padding: 9px 0 9px 10px;
         border-bottom: 1px solid ${colors.GRAY13};
      }
      td { padding: 0; border: 0; }
      td::before { display: none; }
      td[data-label='Distributor'], td[data-label='Buyer'] { grid-area: who; min-width: 0; }
      td[data-label='Distributor'] p, td[data-label='Buyer'] p { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      td[data-label='Status'] { grid-area: status; text-align: right; align-self: start; }
      td[data-label='Credits'] { grid-area: credits; text-align: left; }
      td[data-label='Credits'] span::after { content: ' credits'; font-weight: 500; color: ${colors.GRAY3}; }
      td[data-label='Total'] { grid-area: total; text-align: left; }
      td[data-label='Total'] p { display: inline; margin-left: 4px; }
      td[data-label='When'] { grid-area: when; text-align: right; white-space: nowrap; }
      td[data-label='₱ / credit'] { grid-area: price; }
      td[data-label='₱ / credit']::before { content: '₱ per credit: '; display: inline; font-size: 11.5px; color: ${colors.GRAY3}; }
      td[data-label='Receipt'] { grid-area: receipt; }
      td[data-label='Receipt']:empty { display: none; }
      td.acts { grid-area: acts; text-align: left; }
   }
`;
const DiTr = styled.tr<{ $status: InquiryStatusApi }>`
   td:first-child { box-shadow: inset 3px 0 0 ${({ $status }) => diStatus[$status]}; }
   @media (max-width: 768px) { box-shadow: inset 3px 0 0 ${({ $status }) => diStatus[$status]}; td:first-child { box-shadow: none; } }
`;
const DiName = styled.p`
   margin: 0;
   font-weight: 700;
   line-height: 1.25;
`;
const DiSub = styled.p`
   margin: 1px 0 0;
   font-size: 11.5px;
   color: ${colors.GRAY3};
   line-height: 1.3;
`;
const DiNum = styled.span<{ $hi?: boolean }>`
   display: block;
   font-weight: 700;
   font-variant-numeric: tabular-nums;
   color: ${({ $hi }) => ($hi ? colors.PRIMARY : 'inherit')};
`;
const DiPriceEdit = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 3px;
   font-weight: 700;
`;
const PriceInput = styled.input`
   width: 64px;
   padding: 3px 7px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-weight: 700;
   color: var(--app-text, ${colors.MAIN_DARK});
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;
const DiPill = styled.span<{ $status: InquiryStatusApi }>`
   display: inline-block;
   padding: 2px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   color: ${({ $status }) => diStatus[$status]};
   background: ${({ $status }) => diStatus[$status]}1a;
`;
const DiReason = styled.p`
   margin: 2px 0 0;
   max-width: 100%;
   font-size: 11.5px;
   color: ${colors.DANGER};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const DiLink = styled.a`
   color: ${colors.PRIMARY};
   font-weight: 700;
   font-size: 12.5px;
   text-decoration: none;
   white-space: nowrap;
   &:hover { text-decoration: underline; }
`;
const DiActs = styled.div`
   display: inline-flex;
   gap: 6px;
`;
const ApproveBtn = styled.button`
   background: ${colors.SUCCESS};
   color: white;
   border: none;
   padding: 5px 11px;
   border-radius: 7px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover:not(:disabled) { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const RejectBtn = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}50;
   padding: 4px 10px;
   border-radius: 7px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover:not(:disabled) { background: ${colors.DANGER}10; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EmptyState = styled.div`
   padding: 60px 20px;
   text-align: center;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
`;

const EmptyIcon = styled.div`
   font-size: 40px;
`;

const EmptyText = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
`;

/* ─── Profile pane styles ──────────────────────────────────── */

const ProfileWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   padding: 0 1rem;
   @media (max-width: 768px) { padding: 0 12px; }
`;


const ProfileGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

// Two independent columns so neither leaves trailing white space — each
// stacks its own fields at its natural height. Left = identity + payments,
// right = share link + selling terms.
const ProfileColumns = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 20px;
   align-items: start;
   @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const ProfileCol = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   min-width: 0;
`;

const ProfileField = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const ProfileLbl = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const ProfileSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const ProfileInput = styled.input`
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const ProfileTextarea = styled.textarea`
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   resize: vertical;
   &:focus { border-color: ${colors.PRIMARY}; }
`;


const ProfileAddBtn = styled.button`
   flex-shrink: 0;
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   padding: 8px 14px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover { opacity: 0.9; }
`;


const ProfileAccountsList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const ProfileAccountRow = styled.div`
   display: grid;
   grid-template-columns: auto 1fr auto;
   gap: 12px;
   align-items: center;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
   background: ${colors.WHITE};
`;


const ProfileAccountInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const ProfileAccountName = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const ProfileAccountMono = styled.span`
   font-family: monospace;
   font-size: 12px;
   color: ${colors.GRAY1};
`;

const ProfileAccountSub = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const ProfileAccountRemove = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}40;
   width: 28px;
   height: 28px;
   border-radius: 6px;
   font-size: 13px;
   cursor: pointer;
   &:hover { background: ${colors.DANGER}10; }
`;

const ProfileSaveBar = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 12px;
   padding-top: 16px;
   border-top: 1px solid ${colors.GRAY13};
`;

const ProfileSaveHint = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;

const ProfileSaveBtn = styled.button`
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   padding: 10px 18px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   &:hover:not(:disabled) { opacity: 0.92; }
   &:disabled {
      background: ${colors.GRAY12};
      color: ${colors.GRAY5};
      cursor: not-allowed;
   }
`;

export default Distributor;

// ── Payment methods card (Ray 2026-10-27) ──
const PayCard = styled.div<{ $empty: boolean }>`
   display: grid;
   gap: 12px;
   margin-top: 12px;
   padding: 16px;
   border-radius: 14px;
   border: 1.5px solid ${({ $empty }) => ($empty ? '#f59e0b' : colors.GRAY12)};
   background: ${({ $empty }) =>
      $empty ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.10), rgba(245, 158, 11, 0.02))' : 'linear-gradient(135deg, rgba(55, 96, 249, 0.06), transparent)'};
`;
const PayHead = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const PayEmpty = styled.div`
   display: grid;
   gap: 6px;
   justify-items: start;
   b {
      font-size: 14px;
      color: #b45309;
   }
   span {
      font-size: 13px;
      color: ${colors.GRAY3};
   }
`;
const PayAddBig = styled.button`
   margin-top: 6px;
   padding: 10px 18px;
   border: 0;
   border-radius: 10px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-weight: 800;
   font-size: 14px;
   cursor: pointer;
   &:hover {
      opacity: 0.92;
   }
`;
